import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { Panel, StatusPill, SectionTitle, TimeAgo } from "@/components/sov";
import { Crosshair, Plus, Search, Target, Activity, X } from "lucide-react";

const ProgressBar = ({ value, status }) => {
  const color =
    status === "concluded" ? "#6cc88f" :
    status === "paused"    ? "#9bb1c8" :
                              "#dcb95f";
  return (
    <div className="w-full h-1.5 bg-[var(--obsidian-3)] border border-[color:var(--gold-line)] relative overflow-hidden" data-testid={`progress-${value}`}>
      <div
        className="h-full transition-all"
        style={{
          width: `${value}%`,
          background: `linear-gradient(90deg, ${color}80, ${color})`,
          boxShadow: `0 0 10px ${color}55`,
        }}
      />
    </div>
  );
};

const NewHuntModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({ name: "", hypothesis: "", lead: "M. Saxon", query: "", mitre: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        hypothesis: form.hypothesis,
        lead: form.lead,
        query: form.query,
        mitre_techniques: form.mitre.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.post("/hunts", payload);
      setForm({ name: "", hypothesis: "", lead: "M. Saxon", query: "", mitre: "" });
      onCreated();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" data-testid="new-hunt-modal">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="sov-panel sov-corners w-full max-w-xl relative" style={{ background: "var(--obsidian-2)" }}>
        <span className="corner tl" /><span className="corner tr" />
        <span className="corner bl" /><span className="corner br" />
        <div className="flex items-center justify-between px-5 py-3 border-b border-[color:var(--gold-line)]">
          <div className="flex items-center gap-3">
            <Crosshair size={14} className="text-gold-300" />
            <h3 className="font-display text-[13px] tracking-[0.22em] text-gold-100 uppercase">
              Commission New Hunt
            </h3>
          </div>
          <button onClick={onClose} className="text-ivory-3 hover:text-gold-200" data-testid="close-modal">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Hunt Name</label>
            <input
              data-testid="hunt-name-input"
              required
              className="sov-input mt-1"
              placeholder="e.g. Kerberoast Pivot Trail"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Hypothesis</label>
            <textarea
              data-testid="hunt-hypothesis-input"
              required
              rows={3}
              className="sov-input mt-1"
              placeholder="e.g. Suspect TGT forgery against critical DCs..."
              value={form.hypothesis}
              onChange={(e) => setForm({ ...form, hypothesis: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Lead Analyst</label>
              <input
                data-testid="hunt-lead-input"
                className="sov-input mt-1"
                value={form.lead}
                onChange={(e) => setForm({ ...form, lead: e.target.value })}
              />
            </div>
            <div>
              <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">MITRE (csv)</label>
              <input
                data-testid="hunt-mitre-input"
                className="sov-input mt-1"
                placeholder="T1078, T1059.001"
                value={form.mitre}
                onChange={(e) => setForm({ ...form, mitre: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Detection Query</label>
            <textarea
              data-testid="hunt-query-input"
              rows={2}
              className="sov-input mt-1 font-mono"
              placeholder="process.parent_name:winword.exe AND child:powershell.exe"
              value={form.query}
              onChange={(e) => setForm({ ...form, query: e.target.value })}
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost" data-testid="cancel-hunt-btn">
              Cancel
            </button>
            <button type="submit" disabled={submitting} className="btn-gold" data-testid="submit-hunt-btn">
              {submitting ? "Commissioning…" : "Commission Hunt"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ThreatHunts = () => {
  const [hunts, setHunts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hunts");
      setHunts(data.hunts);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = hunts.filter((h) => {
    if (filter !== "all" && h.status !== filter) return false;
    if (search && !`${h.name} ${h.hypothesis}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const counts = {
    all: hunts.length,
    active: hunts.filter((h) => h.status === "active").length,
    paused: hunts.filter((h) => h.status === "paused").length,
    concluded: hunts.filter((h) => h.status === "concluded").length,
  };

  return (
    <div className="space-y-6" data-testid="threat-hunts">
      <div className="flex items-end justify-between">
        <SectionTitle sub="// MITRE-MAPPED HUNT CHAMBERS">
          Threat Hunts
        </SectionTitle>
        <button
          className="btn-gold mb-6"
          onClick={() => setModalOpen(true)}
          data-testid="new-hunt-btn"
        >
          <Plus size={12} />
          Commission Hunt
        </button>
      </div>

      {/* TOP STATS */}
      <div className="grid grid-cols-4 gap-4" data-testid="hunt-stats">
        {[
          { key: "all", label: "Total Hunts", icon: Target },
          { key: "active", label: "Active", icon: Activity },
          { key: "paused", label: "Paused", icon: Crosshair },
          { key: "concluded", label: "Concluded", icon: Crosshair },
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => setFilter(s.key)}
            className={`sov-panel sov-corners p-5 text-left transition-all ${filter === s.key ? "ring-1 ring-[color:var(--gold-300)]" : ""}`}
            data-testid={`filter-${s.key}`}
          >
            <span className="corner tl" /><span className="corner tr" />
            <span className="corner bl" /><span className="corner br" />
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">{s.label}</div>
                <div className="font-display text-[28px] text-gold-100 mt-1">{counts[s.key]}</div>
              </div>
              <s.icon size={22} className="text-gold-300 opacity-70" />
            </div>
          </button>
        ))}
      </div>

      {/* SEARCH */}
      <div className="flex items-center gap-3">
        <div className="flex-1 relative max-w-md">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-gold-300" />
          <input
            data-testid="hunt-search"
            className="sov-input pl-9"
            placeholder="Filter by name or hypothesis…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-ivory-3">
          {filtered.length} of {hunts.length}
        </span>
      </div>

      {/* HUNT GRID */}
      {loading ? (
        <div className="text-center py-20"><span className="dot-loader"><span /><span /><span /></span></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="hunt-grid">
          {filtered.map((h) => (
            <div key={h.id} className="sov-panel sov-corners p-5" data-testid={`hunt-card-${h.id.slice(0, 6)}`}>
              <span className="corner tl" /><span className="corner tr" />
              <span className="corner bl" /><span className="corner br" />
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.22em] text-gold-300 mb-1">
                    {h.name.split(" / ")[0]}
                  </div>
                  <h3 className="font-display text-[15px] tracking-[0.12em] text-gold-100">
                    {h.name.split(" / ").slice(1).join(" / ") || h.name}
                  </h3>
                </div>
                <StatusPill status={h.status} />
              </div>
              <p className="font-serif italic text-[13px] text-ivory-2 leading-snug mb-4">
                "{h.hypothesis}"
              </p>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div>
                  <div className="font-mono text-[9px] tracking-[0.18em] text-ivory-3 uppercase">Progress</div>
                  <div className="font-display text-[15px] text-gold-100 mt-1">{h.progress}%</div>
                </div>
                <div>
                  <div className="font-mono text-[9px] tracking-[0.18em] text-ivory-3 uppercase">IOCs</div>
                  <div className="font-display text-[15px] text-gold-100 mt-1">{h.iocs}</div>
                </div>
                <div>
                  <div className="font-mono text-[9px] tracking-[0.18em] text-ivory-3 uppercase">Findings</div>
                  <div className="font-display text-[15px] text-gold-100 mt-1">{h.findings}</div>
                </div>
              </div>
              <ProgressBar value={h.progress} status={h.status} />
              <div className="mt-4">
                <div className="font-mono text-[9px] tracking-[0.18em] text-ivory-3 uppercase mb-2">
                  MITRE Techniques
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {h.mitre_techniques.map((t) => (
                    <span
                      key={t}
                      className="font-mono text-[9.5px] px-2 py-0.5 border border-[color:var(--gold-line)] text-gold-200 tracking-[0.1em]"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              {h.query && (
                <div className="mt-3 p-2 bg-[var(--obsidian-1)] border border-[color:var(--gold-line)]">
                  <div className="font-mono text-[8.5px] tracking-[0.2em] text-ivory-3 uppercase mb-1">Detection Query</div>
                  <code className="font-mono text-[10.5px] text-gold-100 break-words">{h.query}</code>
                </div>
              )}
              <div className="mt-4 flex items-center justify-between text-[10px]">
                <div className="font-mono text-ivory-3 tracking-[0.14em] uppercase">
                  Lead: <span className="text-ivory-2">{h.lead}</span>
                </div>
                <TimeAgo iso={h.created_at} />
              </div>
            </div>
          ))}
        </div>
      )}

      <NewHuntModal open={modalOpen} onClose={() => setModalOpen(false)} onCreated={load} />
    </div>
  );
};

export default ThreatHunts;
